import { Layout } from '@components/common'
import { getConfig } from '@framework/api'
import getSiteInfo from '@framework/common/get-site-info'
import { getPageData } from '@utils/contentful/get-page-data'
import { revalidatePeriod } from '@utils/server-side-config'
import cn from 'classnames'
import { Entry } from 'contentful'
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import React, { useEffect, useState } from 'react'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import s from './About.module.css'
import Link from '@components/common/NoPrefetchLink'
import { GenericContentBlock } from 'types/contentful/genericContentBlock'
import { ContentfulNextJsImage } from '@components/common/ContentfulImage'
import { mapContentfulImageAsset } from '@utils/contentful/contentfulImage'
import { NavLinks } from '@pageFeatures/about-us/NavLinks'
import { OurTeam } from '@pageFeatures/about-us/OurTeam/OurTeam'
import { Transparency } from '@pageFeatures/about-us/Transparency/Transparency'
import pageContent from 'pageContent/about-us.content.json'

/* Fetch data from contentful Module */
export async function getStaticProps({
  preview,
  locale,
}: GetStaticPropsContext) {
  const config = getConfig({ locale })
  const { categories } = await getSiteInfo({ config, preview })
  const pageData = await getPageData({ categories, preview })

  return {
    props: {
      ...pageData,
    },
    revalidate: revalidatePeriod(),
  }
}

export default function About() {
  /* On load call to fetch Data() and set data to state */
  const [treeCounter, setTreeCounter] = useState('')
  useEffect(() => {
    async function fetchDataFromAPI() {
      await fetch(`/api/customer/treeplantform`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.total) {
            setTreeCounter(response.total.toLocaleString())
          }
        })
    }
    fetchDataFromAPI()
  }, [])

  /* Why create section slick slider */
  const whyCreateSettings = {
    className: 'slider variable-width',
    dots: false,
    arrows: false,
    infinite: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 767,
        settings: {
          dots: true,
          slidesToShow: 1,
          variableWidth: true,
        },
      },
    ],
  }

  // Content for this page is temporarily stored statically in a JSON file.
  // This allows us free up obsolete contentful models that were previously used for this page content.
  const {
    aboutPageData,
    aboutPageData2,
  }: {
    aboutPageData: any
    aboutPageData2: any
  } = pageContent

  /* Alliance & Suppliers Section */
  const [switchSection, setswitchSection] = useState('alliance')
  const changeSwitchTab = (text: React.SetStateAction<string>) => {
    setswitchSection(text)
  }
  /* Join Us Popup Section */
  const [joinusPopup, setjoinusPopup] = useState(false)
  const switchJoinUsPopup = (text: React.SetStateAction<boolean>) => {
    setjoinusPopup(text)
  }
  /* Supplychain Tab Section */
  const [supplychainTab, setsupplychainTab] = useState('supplier')
  const switchSupplychainTab = (text: React.SetStateAction<string>) => {
    setsupplychainTab(text)
  }
  /* Video Popup Section */
  const [videoPopup, setVideoPopup] = useState(false)
  const openVideoPopup = (text: React.SetStateAction<boolean>) => {
    setVideoPopup(text)
  }

  useEffect(() => {
    const handleHashChange = () => {
      window.history.pushState(
        {
          ...window.history.state,
          as: window.location.pathname,
          url: window.location.pathname,
        },
        '',
        window.location.pathname
      )
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  return (
    <div className={s.container}>
      {/* Section Navigation Menu */}
      <style>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      {aboutPageData.fields.navList ? (
        <NavLinks navList={aboutPageData.fields.navList} />
      ) : null}

      {/* About Banner */}
      <div className={s.heroAboutUs}>
        <div
          className={s.whatIsPos}
          onClick={() => {
            openVideoPopup(true)
          }}
        >
          <img
            className="w-full"
            src={aboutPageData.fields.bannerImage.fields.file.url}
            alt={aboutPageData.fields.bannerImage.fields.title}
          />
        </div>
        {videoPopup && (
          <div className={s.videoPopupSection}>
            <div
              className={`${s.videoPopupOverlay} fixed inset-0 z-10 overflow-auto bg-black opacity-90`}
              onClick={() => {
                openVideoPopup(false)
              }}
            ></div>
            <div
              className={`${s.videoPopupBox} left-1/2 top-1/2 transform -translate-y-1/2 -translate-x-1/2 m-0 bottom-auto h-auto fixed right-0 z-20`}
            >
              <div className="vimeoVideoContainer">
                <span
                  className={s.popupClose}
                  onClick={() => {
                    openVideoPopup(false)
                  }}
                >
                  {' '}
                  ×{' '}
                </span>
                <div id="vimeo_video_container_content">
                  <iframe
                    src={`${aboutPageData.fields.bannerVideoLink}?controls=0&disablekb=1&playsinline=1&autoplay=1&muted=0&enablejsapi=1&showinfo=0&transparent=0&loop=1&playlist=Bdwn0GaiHE4&modestbranding=1`}
                    width="100%"
                    allow="autoplay"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* What is noissue */}
      <div className={s.whatIsNoissue} id="why-we-exist">
        <div
          className={`${s.fixContainer} px-4 m-auto relative`}
          style={{
            background: `url('${aboutPageData.fields.noissueBackgroundImage.fields.file.url}') no-repeat`,
          }}
        >
          <h2 className={s.headerTitle}>
            {aboutPageData.fields.noissueHeading}
          </h2>
          <div className={s.whatisInner}>
            {aboutPageData.fields.noissueDescription.content &&
              aboutPageData.fields.noissueDescription.content.map(
                (item: any, index: number) => {
                  return (
                    <p key={index}>
                      {item.content.map(
                        (contentsItem: any, contentsIndex: number) => {
                          return (
                            <span key={contentsIndex}>
                              {contentsItem.value == 'noissue' && (
                                <strong className="About_logoClr__SXMMQ">
                                  {contentsItem.value}
                                </strong>
                              )}
                              {contentsItem.value == 'custom' && (
                                <strong className="About_textBlue__27vSt">
                                  {contentsItem.value}
                                </strong>
                              )}
                              {contentsItem.value == 'sustainable' && (
                                <strong className="About_textGrnClr__1iAqc">
                                  {contentsItem.value}
                                </strong>
                              )}
                              {contentsItem.value == 'packaging' && (
                                <strong className="About_logoClr__SXMMQ">
                                  {contentsItem.value}
                                </strong>
                              )}
                              {contentsItem.value != 'noissue' &&
                                contentsItem.value != 'custom' &&
                                contentsItem.value != 'sustainable' &&
                                contentsItem.value != 'packaging' && (
                                  <span>{contentsItem.value}</span>
                                )}
                            </span>
                          )
                        }
                      )}
                    </p>
                  )
                }
              )}
          </div>
        </div>
      </div>

      {/* Why create with us AND products list */}
      <div className={s.whyCreateWith}>
        <div className={s.whyCreateRow}>
          <div className={s.fixcontainer}>
            <h3 className="mt-8 mb-12 text-4xl font-bold text-center text-acai md:text-5xl md:mb-24">
              {aboutPageData.fields.whyCreateSectionHeading}
            </h3>
            <div className="owlCarousel" id="why-create-inner">
              <Slider {...whyCreateSettings}>
                {aboutPageData.fields.whyCreateList.map(
                  (item: Entry<GenericContentBlock>) => {
                    const image = mapContentfulImageAsset(
                      item?.fields?.primaryImages?.[0]
                    )
                    return (
                      <div key={item?.fields?.h1} className={s.item}>
                        <ContentfulNextJsImage
                          alt={image?.imageTitle}
                          src={image?.imageUrl}
                          width={370}
                          height={235} // FIXME: No image optimized?
                        />
                        <div className={s.whyCreateContent}>
                          <h4 className="text-3xl text-acai lg:mb-6 xl:font-bold xl:mb-4">
                            {item?.fields?.h1}
                          </h4>
                          <div className="text-xl font-normal text-core-grey-darkest md:pb-6">
                            {item?.fields?.h2}
                          </div>
                        </div>
                      </div>
                    )
                  }
                )}
              </Slider>
            </div>
          </div>
        </div>
      </div>

      <Transparency {...aboutPageData.transparency} />

      {/* Eco Alliance AND Suppliers sections */}
      <div id="eco-alliance" className={s.ecoAllianceAndSuppliersSection}>
        {switchSection == 'alliance' && (
          <>
            <div className={cn(s.ecoAllianceSection, s.activeSection)}>
              <div
                className={`${s.topLogo} absolute -top-0 -left-0 -right-0 m-auto inline-block`}
              >
                <img
                  src={
                    aboutPageData.fields.ecoAllianceTopBadgeImage.fields.file
                      .url
                  }
                  alt={
                    aboutPageData.fields.ecoAllianceTopBadgeImage.fields.title
                  }
                />
              </div>
              <div className={s.topContainer}>
                <div className={s.sectionSelectionBtns}>
                  <div className={cn(s.tabs, s.allianceBtnm, s.active)}>
                    <span>{aboutPageData.fields.ecoAllianceTab1Title}</span>
                  </div>
                  <div
                    className={cn(s.tabs, s.supplierBtn)}
                    onClick={() => {
                      changeSwitchTab('suppliers')
                    }}
                  >
                    <span>{aboutPageData.fields.ecoAllianceTab2Title}</span>
                  </div>
                </div>
                <div className={s.supplychainTitle}>
                  <div className={s.title}>
                    {aboutPageData.fields.ecoAllianceHeading}
                  </div>
                  <div className={s.desc}>
                    {aboutPageData.fields.ecoAllianceSubHeading}
                  </div>
                </div>
              </div>
              <div className={s.containerSection}>
                <div
                  className={`${cn(
                    s.ecoPackagingMap,
                    s.ecoPackagingNewmap
                  )} relative m-auto`}
                >
                  <img
                    className={s.noissueEcoMapImage}
                    src={
                      aboutPageData.fields.ecoAllianceBackgroundImage.fields
                        .file.url
                    }
                    alt={
                      aboutPageData.fields.ecoAllianceBackgroundImage.fields
                        .title
                    }
                  />
                  <div className={cn(s.mapcursor, s.treecursor1)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>BRITISH COLUMBIA</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor2)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>COLORADO</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor3)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>HAITI</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor4)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>GUATEMALA</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor5)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>AMAZON</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor6)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>BRAZIL</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor7)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>GHANA</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor8)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>VIETNAM</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor9)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>INDONESIA</span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.treecursor10)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/eco-tree-icon.png" alt="" />
                      <span>AUSTRALIA</span>
                    </div>
                  </div>
                  <div
                    className={cn(s.mapcursor, s.mapcursorIcon, s.cursorTenCo)}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/Icons_Ten_Co.png" alt="" />
                      <span>
                        Ten & Co.
                        <a href="/blog/ten-and-co/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorTheAyla
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/icons-the-ayla-view.png"
                        alt=""
                      />
                      <span>
                        Sheertex
                        <a
                          href="/blog/ayla-view-wedding-stationery-with-a-luxury-twist/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorNomadBotanicals
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/Icons_Nomad_Botanicals.png"
                        alt=""
                      />
                      <span>
                        Nomad Botanicals
                        <a href="/blog/nomad-botanicals/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorKimSielbeck
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/Icons_Kim_Sielbeck.png" alt="" />
                      <span>
                        Kim Sielbeck
                        <a href="/blog/kim-sielbeck/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorShowfields
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/Icons_Showfields.png" alt="" />
                      <span>
                        Showfields
                        <a
                          href="/blog/showfields-an-immersive-retail-experience-for-the-holidays/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorDearPrudence
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/icons-dear-prudence.png"
                        alt=""
                      />
                      <span>
                        Dear prudence
                        <a href="/blog/dear-prudence-studio/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div className={cn(s.mapcursor, s.mapcursorIcon, s.cursorCo)}>
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/Icons_1820co.png" alt="" />
                      <span>
                        1820co.
                        <a href="/blog/1820-co/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorKCreations
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/Icons_K-Creations.png" alt="" />
                      <span>
                        K. Creations
                        <a href="/blog/k-creations/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorNicoNeco
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/Icons_Nico_Neco.png" alt="" />
                      <span>
                        Nico Neco
                        <a href="/blog/niconeco-zakkaya/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorLittleChicagoClothing
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/icons-little-chicago-clothing.png"
                        alt=""
                      />
                      <span>
                        Little Chicago Clothing
                        <a
                          href="/blog/little-chicago-clothing/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorOakStone
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/Icons_Oak_Stone.png" alt="" />
                      <span>
                        Oak & Stone
                        <a
                          href="/blog/how-online-only-brand-oak-and-stone-clothing-co-used-custom-packaging-to-boost-the-customer-experience/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorTerraTory
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/Icons_Terratory.png" alt="" />
                      <span>
                        Terra-Tory
                        <a
                          href="/blog/unboxing-with-terra-tory/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorKalosKopic
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-kalos-kopic.png" alt="" />
                      <span>
                        Kalos kopic
                        <a
                          href="/blog/kaloskopic-challenging-fast-fashion-through-sustainable-design/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorNoahsBox
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-noahs-box.png" alt="" />
                      <span>
                        Noahs box
                        <a
                          href="/blog/creating-a-memorable-unboxing-experience-with-noahs-box/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(s.mapcursor, s.mapcursorIcon, s.cursorYala)}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-yala.png" alt="" />
                      <span>
                        Yala
                        <a href="/blog/yala-jewellery/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorLetterLoft
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-letter-loft.png" alt="" />
                      <span>
                        Letter loft
                        <a href="/blog/the-letter-loft/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorJacquelineColley
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/icons-jacqueline-colley.png"
                        alt=""
                      />
                      <span>
                        Jacqueline colley
                        <a href="/blog/jacqueline-colley/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorAljaHorvat
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-alja-horvat.png" alt="" />
                      <span>
                        Alja horvat
                        <a
                          href="/blog/how-i-fulfill-alja-horvat/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorNinaStajner
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-nina-stajner.png" alt="" />
                      <span>
                        Nina stajner
                        <a
                          href="/blog/how-illustrator-nina-stajner-took-advantage-of-noissues-offline-design-tool/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorLauraLhuillier
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/icons-laura-lhuillier.png"
                        alt=""
                      />
                      <span>
                        Laura lhuillier
                        <a
                          href="/blog/plastic-free-packaging-with-laura-lhuillier/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorKarlaCola
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-karla-cola.png" alt="" />
                      <span>
                        Karla cola
                        <a href="/blog/karla-cola/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorJerichoRoadClothing
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/icons-jericho-road-clothing.png"
                        alt=""
                      />
                      <span>
                        Jericho road clothing
                        <a href="/blog/jericho-road-clothing/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorAmericanDesignClub
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/Icons_American_Design_Club.png"
                        alt=""
                      />
                      <span>
                        American Design Club
                        <a href="/blog/american-design-club/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorBoxxKids
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-boxx-kids.png" alt="" />
                      <span>
                        Boxx kids
                        <a
                          href="/blog/boxx-kids-sustainable-packaging-for-ethical-kids-clothes/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorPaigeTuzee
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-paige-tuzee.png" alt="" />
                      <span>
                        Paige tuzee
                        <a href="/blog/paige-tuzee-designs/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorSansBeast
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-sans-beast.png" alt="" />
                      <span>
                        Sans beast
                        <a href="/blog/sans-beast/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorFranksLee
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-franks-lee.png" alt="" />
                      <span>
                        Franks lee
                        <a
                          href="/blog/franks-lee-lowering-our-impact-with-compostable-mailers/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorBlushFlowers
                    )}
                  >
                    <div className={s.countryHover}>
                      <img src="/aboutus/icons/icons-blush.png" alt="" />
                      <span>
                        Blush flowers
                        <a href="/blog/blush-flowers/" target="_blank">
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      s.mapcursor,
                      s.mapcursorIcon,
                      s.cursorTheBookPlayBox
                    )}
                  >
                    <div className={s.countryHover}>
                      <img
                        src="/aboutus/icons/icons-the-book-play-box.png"
                        alt=""
                      />
                      <span>
                        The book play box
                        <a
                          href="/blog/designing-subscription-box-packaging-with-the-book-playbox/"
                          target="_blank"
                        >
                          View Case Study
                        </a>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={s.bottomSection}>
                <div
                  className={`${s.tressPlanet} relative w-auto inline-block`}
                  data-left={aboutPageData.fields.ecoAllianceTreeLeftTitle}
                  data-right={aboutPageData.fields.ecoAllianceTreeRightTitle}
                >
                  <img
                    className="align-middle"
                    src={
                      aboutPageData.fields.ecoAllianceTreeImage.fields.file.url
                    }
                    alt={aboutPageData.fields.ecoAllianceTreeImage.fields.title}
                  />
                </div>
                <div className={s.countBlock}>{treeCounter || '0'}</div>
              </div>
            </div>
            <div className={s.aboutPrgSection}>
              <div className={s.fixContainer}>
                <div className={s.aboutProgramLeft}>
                  <h3>{aboutPageData.fields.ecoAllianceAboutTitle}</h3>
                  <div className={s.aboutProgramLeftContent}>
                    {aboutPageData.fields.ecoAllianceAboutDescription.content &&
                      aboutPageData.fields.ecoAllianceAboutDescription.content.map(
                        (item: any, index: number) => {
                          return (
                            <div key={index}>
                              {item.content.map(
                                (contentsItem: any, contentsIndex: number) => {
                                  return (
                                    <p key={contentsIndex}>
                                      {contentsItem.value}
                                    </p>
                                  )
                                }
                              )}
                            </div>
                          )
                        }
                      )}
                  </div>

                  <Link href={`/community/eco-alliance/`}>
                    <a
                      className={`${s.joinUsBtn} joinButton btn`}
                      href="/community/eco-alliance/"
                    >
                      {aboutPageData.fields.ecoAllianceJoinButtonLabel}
                    </a>
                  </Link>
                </div>
                <div className={s.aboutProgramDaigram}>
                  <div className={s.ecoAboutProgram}>
                    <div className={s.aboutEcoLogo}>
                      <img
                        src={
                          aboutPageData.fields.ecoAllianceEcoBadgeImage.fields
                            .file.url
                        }
                        alt={
                          aboutPageData.fields.ecoAllianceEcoBadgeImage.fields
                            .title
                        }
                      />
                    </div>
                    <div className={s.sportSupport}>
                      <h4>{aboutPageData.fields.ecoAllianceEcoTitle}</h4>
                      <p>{aboutPageData.fields.ecoAllianceEcoDescription}</p>
                    </div>
                  </div>
                  <div className={s.aboutEcoDaigramImg}>
                    <img
                      src={
                        aboutPageData.fields.ecoAllianceEcoStoreDiagram.fields
                          .file.url
                      }
                      alt={
                        aboutPageData.fields.ecoAllianceEcoStoreDiagram.fields
                          .title
                      }
                    />
                  </div>
                </div>
              </div>
              <div className={`${s.aboutBg} w-full relative`}>
                <img
                  className="w-full"
                  src={
                    aboutPageData.fields.ecoAllianceBottomImage.fields.file.url
                  }
                  alt={aboutPageData.fields.ecoAllianceBottomImage.fields.title}
                />
              </div>
            </div>
            {joinusPopup && (
              <div className={s.joinUsPopupSection}>
                <div
                  className={s.joinUsPopupOverlay}
                  onClick={() => {
                    switchJoinUsPopup(false)
                  }}
                ></div>
                <div className={s.joinUsPopupBox}>
                  <div className={s.popupContainer}>
                    <span
                      className={s.opupCloseBtn}
                      onClick={() => {
                        switchJoinUsPopup(false)
                      }}
                    >
                      ×
                    </span>
                    <div className={s.contentArea}>
                      <div className={s.heading}>
                        {aboutPageData.fields.ecoAllianceJoinPopupTitle}
                      </div>
                      <form method="POST" name="popupEcoPackagingAllianceForm">
                        <input
                          type="email"
                          placeholder={
                            aboutPageData.fields.ecoAllianceJoinPopupPlaceholder
                          }
                          name="ecom_popup_email"
                          className={s.inputText}
                        />
                        <button type="button" className={s.joinUsBtn}>
                          {aboutPageData.fields.ecoAllianceJoinPopupButtonLabel}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {switchSection == 'suppliers' && (
          <div className={cn(s.suppliersSection, s.activeSection)}>
            <div className={s.ecoAllianceSection}>
              <div
                className={`${s.topLogo} absolute -top-0 -left-0 -right-0 m-auto inline-block`}
              >
                <img
                  src={
                    aboutPageData2.fields.suppliersTopBadgeImage.fields.file.url
                  }
                  alt={
                    aboutPageData2.fields.suppliersTopBadgeImage.fields.title
                  }
                />
              </div>
              <div className={s.topContainer}>
                <div className={s.sectionSelectionBtns}>
                  <div
                    className={cn(s.tabs, s.allianceBtn)}
                    onClick={() => {
                      changeSwitchTab('alliance')
                    }}
                  >
                    <span>{aboutPageData2.fields.suppliersTab1Title}</span>
                  </div>
                  <div className={cn(s.tabs, s.supplierBtn, s.active)}>
                    <span>{aboutPageData2.fields.suppliersTab2Title}</span>
                  </div>
                </div>
                <div className={s.supplychainTitle}>
                  <div
                    className={`${s.title} text-2xl md:text-3xl font-bold mb-3`}
                  >
                    {aboutPageData2.fields.suppliersHeading}
                  </div>
                  <div className={`${s.desc} text-lg font-light`}>
                    {aboutPageData2.fields.suppliersSubHeading}
                  </div>
                </div>
              </div>
              <div className={s.containerSection}>
                <div className={s.supplychainMap}>
                  <img
                    src={
                      aboutPageData2.fields.suppliersBackgroundImage.fields.file
                        .url
                    }
                    alt={
                      aboutPageData2.fields.suppliersBackgroundImage.fields
                        .title
                    }
                  />
                </div>
              </div>
              <div className={s.distroBadgeLogo}>
                <img
                  src={
                    supplychainTab.includes('supplier')
                      ? aboutPageData2.fields.suppliersTopBadgeImage.fields.file
                          .url
                      : aboutPageData2.fields.suppliersDistributorImage.fields
                          .file.url
                  }
                  alt={
                    supplychainTab.includes('supplier')
                      ? aboutPageData2.fields.suppliersTopBadgeImage.fields
                          .title
                      : aboutPageData2.fields.suppliersDistributorImage.fields
                          .title
                  }
                />
              </div>
            </div>
            <div className={cn(s.aboutPrgSection, s.supplyPrgSection)}>
              <div className={s.fixContainer}>
                <div className={s.aboutProgramLeft}>
                  <h3 className="mb-5 text-3xl font-bold">
                    {aboutPageData2.fields.suppliersSupplyChainTitle}
                  </h3>
                  <div className={`${s.allianDesc} text-base mb-10`}>
                    {aboutPageData2.fields.suppliersSupplyChainDescription
                      .content &&
                      aboutPageData2.fields.suppliersSupplyChainDescription.content.map(
                        (item: any, index: number) => {
                          return (
                            <>
                              <p key={index}>
                                {item.content.map(
                                  (
                                    contentsItem: any,
                                    contentsIndex: number
                                  ) => {
                                    return (
                                      <>
                                        {contentsItem.nodeType ==
                                        'hyperlink' ? (
                                          <a
                                            key={contentsIndex}
                                            href={contentsItem.data.uri}
                                          >
                                            {contentsItem.content[0].value}
                                          </a>
                                        ) : (
                                          <span key={contentsIndex}>
                                            {contentsItem.value}
                                          </span>
                                        )}
                                      </>
                                    )
                                  }
                                )}
                              </p>
                              <br />
                            </>
                          )
                        }
                      )}
                  </div>
                </div>
                <div className={s.supplychainDaigramTabWrap}>
                  <div className={s.supplierTabNavs}>
                    <div
                      className={`${cn(s.supplierTabs, s.supplierTab)} ${
                        supplychainTab == 'supplier' && s.active
                      }`}
                      onClick={() => {
                        switchSupplychainTab('supplier')
                      }}
                    >
                      <span>
                        {aboutPageData2.fields.suppliersSupplyTab1Title}
                      </span>
                    </div>
                    <div
                      className={`${cn(s.supplierTabs, s.distroTab)} ${
                        supplychainTab == 'distro' && s.active
                      }`}
                      onClick={() => {
                        switchSupplychainTab('distro')
                      }}
                    >
                      <span>
                        {aboutPageData2.fields.suppliersSupplyTab2Title}
                      </span>
                    </div>
                  </div>
                  <div className={s.tabContent}>
                    {supplychainTab == 'supplier' && (
                      <div className="tabPanel supplierTabText">
                        <h4>
                          {aboutPageData2.fields.suppliersSupplyTab1Heading}
                        </h4>
                        <div className={s.tabDesc}>
                          {aboutPageData2.fields.suppliersSupplyTab1Description
                            .content &&
                            aboutPageData2.fields.suppliersSupplyTab1Description.content.map(
                              (item: any, index: number) => {
                                return (
                                  <>
                                    {item.content[0].nodeType == 'list-item' ? (
                                      <ul key={index}>
                                        {item.content.map(
                                          (
                                            contentsItem: any,
                                            contentsIndex: number
                                          ) => {
                                            return (
                                              <>
                                                {contentsItem.nodeType ==
                                                  'list-item' && (
                                                  <li key={contentsIndex}>
                                                    {
                                                      contentsItem.content[0]
                                                        .content[0].value
                                                    }
                                                  </li>
                                                )}
                                              </>
                                            )
                                          }
                                        )}
                                      </ul>
                                    ) : (
                                      <p key={index}>
                                        {item.content.map(
                                          (
                                            contentsItem: any,
                                            contentsIndex: number
                                          ) => {
                                            return (
                                              <>
                                                {contentsItem.nodeType !=
                                                  'list-item' && (
                                                  <span key={contentsIndex}>
                                                    {contentsItem.value}
                                                  </span>
                                                )}
                                              </>
                                            )
                                          }
                                        )}
                                      </p>
                                    )}
                                  </>
                                )
                              }
                            )}
                        </div>
                        <br />
                        <a
                          className={`${s.getInTouchBtn} btn`}
                          href={
                            aboutPageData2.fields.suppliersSupplyTab1ButtonLink
                          }
                        >
                          {aboutPageData2.fields.suppliersSupplyTab1ButtonLabel}
                        </a>
                      </div>
                    )}
                    {supplychainTab == 'distro' && (
                      <div className="tabPanel distroTabText">
                        <h4>
                          {aboutPageData2.fields.suppliersSupplyTab2Heading}
                        </h4>
                        <div className={s.tabDesc}>
                          {aboutPageData2.fields.suppliersSupplyTab2Description
                            .content &&
                            aboutPageData2.fields.suppliersSupplyTab2Description.content.map(
                              (item: any, index: number) => {
                                return (
                                  <>
                                    {item.content[0].nodeType == 'list-item' ? (
                                      <ul key={index}>
                                        {item.content.map(
                                          (
                                            contentsItem: any,
                                            contentsIndex: number
                                          ) => {
                                            return (
                                              <>
                                                {contentsItem.nodeType ==
                                                  'list-item' && (
                                                  <li key={contentsIndex}>
                                                    {
                                                      contentsItem.content[0]
                                                        .content[0].value
                                                    }
                                                  </li>
                                                )}
                                              </>
                                            )
                                          }
                                        )}
                                      </ul>
                                    ) : (
                                      <p key={index}>
                                        {item.content.map(
                                          (
                                            contentsItem: any,
                                            contentsIndex: number
                                          ) => {
                                            return (
                                              <>
                                                {contentsItem.nodeType !=
                                                  'list-item' && (
                                                  <span key={contentsIndex}>
                                                    {contentsItem.value}
                                                  </span>
                                                )}
                                              </>
                                            )
                                          }
                                        )}
                                      </p>
                                    )}
                                  </>
                                )
                              }
                            )}
                        </div>
                        <br />
                        <a
                          className={`${s.getInTouchBtn} btn`}
                          href={
                            aboutPageData2.fields.suppliersSupplyTab2ButtonLink
                          }
                        >
                          {aboutPageData2.fields.suppliersSupplyTab2ButtonLabel}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={`${s.aboutBg} w-full relative`}>
                <img
                  className="w-full"
                  src={
                    aboutPageData2.fields.suppliersBottomImage.fields.file.url
                  }
                  alt={aboutPageData2.fields.suppliersBottomImage.fields.title}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Our partner */}
      <div className={s.ourPrtnerAbout} id="our-partner">
        <div
          className={cn(
            'absolute z-10 hidden m-auto text-sm -top-0 -bottom-0 left-16 h-14 rounded-3xl xl:block',
            'text-pitaya bg-white w-[121px] transform rotate-90 shadow-[0_1px_4px_0_rgba(23,23,44,0.14)]'
          )}
        >
          <span className="inline-block text-xl font-bold text-center transform rotate-180 w-[121px] h-[40px] leading-[40px]">
            {aboutPageData2.fields.partnerSectionLeftHeading}
          </span>
        </div>
        <div className={s.fixContainer}>
          <div className={s.ourPrtnerLft}>
            <img alt="" src="/aboutus/about-partners-img.png" />
          </div>
          <div className={s.ourPrtnerRgt}>
            <h3>{aboutPageData2.fields.partnerSectionHeading}</h3>
            <div className={s.partnerSectionDescription}>
              {aboutPageData2.fields.partnerSectionDescription.content &&
                aboutPageData2.fields.partnerSectionDescription.content.map(
                  (item: any, index: number) => {
                    return (
                      <p key={index}>
                        {item.content.map(
                          (contentsItem: any, contentsIndex: number) => {
                            return (
                              <span key={contentsIndex}>
                                {contentsItem.value}
                              </span>
                            )
                          }
                        )}
                      </p>
                    )
                  }
                )}
            </div>
            <div className="flex justify-between px-10 lg:px-0">
              {aboutPageData2.fields.partnerList.map(
                (item: Entry<GenericContentBlock>, index: number) => {
                  const image = mapContentfulImageAsset(
                    item?.fields?.primaryImages?.[0]
                  )
                  return (
                    <div key={index} className="px-1 text-center">
                      <ContentfulNextJsImage
                        height={130}
                        width={130}
                        alt={image?.imageTitle}
                        src={image?.imageUrl} // FIXME: No image optimized?
                      />
                      <div>
                        <p className="mt-5 text-lg xl:w-full xl:my-5 xl:text-xl xl:text-content-base">
                          {item?.fields?.h1}
                        </p>
                        <a
                          href={item?.fields?.ctaLink}
                          className="inline-block mt-4 text-lg font-bold underline text-acai md:text-xl hover:no-underline"
                        >
                          {item?.fields?.ctaLabel}
                        </a>
                      </div>
                    </div>
                  )
                }
              )}
            </div>
          </div>
        </div>
      </div>

      <OurTeam
        teamSectionHeading={aboutPageData2.fields.teamSectionHeading}
        teamSectionDescription={aboutPageData2.fields.teamSectionDescription}
        teamList={aboutPageData2.fields.teamList}
        teamButtonLink={aboutPageData2.fields.teamButtonLink}
        teamButtonLabel={aboutPageData2.fields.teamButtonLabel}
      />

      {/* Our bottom image */}
      <div className={`${s.aboutBottomBg} w-full`}>
        <img
          className="w-full"
          alt={aboutPageData2.fields.bottomBackgroundImage.fields.title}
          src={aboutPageData2.fields.bottomBackgroundImage.fields.file.url}
        />
      </div>
    </div>
  )
}

About.Layout = Layout
